/**
 * extractDurationOrDistance
 *
 * Given a free-text workout title (e.g. "45 min Easy", "10 km Long Run",
 * "1:30 Long", "1h30 tempo", "5-mile shakeout"), return a compact
 * "45 min" or "10 km" summary suitable for the Program Builder's
 * "Total time or distance" field.
 *
 * Returns null when the title doesn't obviously contain a duration or
 * distance — the coach then fills the field manually. We deliberately
 * bias toward false negatives (leave blank) over false positives
 * (guess wrong) because an incorrect summary would silently affect
 * volume rollups downstream.
 *
 * Match priority: hh:mm > hour+min shorthand > explicit unit (km/mi/m/min/hr) > null.
 */
export function extractDurationOrDistance(title) {
  if (!title || typeof title !== "string") return null;
  const s = title.trim();
  if (!s) return null;

  // Prefer distance over duration when both are present — coaches almost
  // always describe runs by distance if it's given ("10 km long run" is
  // a 10-km run regardless of any duration hint like "for 90 min"). Try
  // distance first, then fall back to duration.
  const distance = extractDistance(s);
  if (distance) return distance;
  return extractDuration(s);
}


function extractDistance(s) {
  // Match: "10 km", "10km", "10.5 km", "5 mi", "5 miles", "800m", "1600 m".
  // Word boundary on both sides so "1 mile time trial" matches but
  // "5-week block" doesn't accidentally capture "5-w".
  const km = s.match(/\b(\d+(?:\.\d+)?)\s*(km|k(?![a-z])|kilometers?|kilometres?)\b/i);
  if (km) {
    const n = parseFloat(km[1]);
    if (Number.isFinite(n)) return `${trimTrailingZero(n)} km`;
  }
  const mi = s.match(/\b(\d+(?:\.\d+)?)\s*(mi|miles?)\b/i);
  if (mi) {
    const n = parseFloat(mi[1]);
    if (Number.isFinite(n)) return `${trimTrailingZero(n)} mi`;
  }
  // Meters — only allow explicit "m" or "meters" so we don't grab "6 min".
  const m = s.match(/\b(\d+)\s*(m(?![a-z])|meters?|metres?)\b/i);
  if (m) {
    const n = parseInt(m[1], 10);
    if (Number.isFinite(n) && n >= 100) return `${n} m`;
  }
  return null;
}


function extractDuration(s) {
  // hh:mm (skip if it's actually a pace like "5:30/km" — that would
  // include a slash).
  if (!s.includes("/")) {
    const hhmm = s.match(/\b(\d+):(\d{2})\b/);
    if (hhmm) {
      const h = parseInt(hhmm[1], 10);
      const m = parseInt(hhmm[2], 10);
      if (Number.isFinite(h) && Number.isFinite(m)) {
        const total = h * 60 + m;
        return total < 60 ? `${total} min` : `${h}h${m === 0 ? "" : String(m).padStart(2, "0")}`;
      }
    }
  }
  // "1h30", "1h 30m", "2h" — matches similar patterns to the duration
  // parser lib but returns a display string, not a number.
  const shortHm = s.match(/\b(\d+)h(\d{1,2})(?!\d)/i);
  if (shortHm) {
    const h = parseInt(shortHm[1], 10);
    const m = parseInt(shortHm[2], 10);
    return `${h}h${String(m).padStart(2, "0")}`;
  }
  const hoursOnly = s.match(/\b(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)(?![a-z])/i);
  const minsOnly = s.match(/\b(\d+(?:\.\d+)?)\s*(?:minutes?|mins?|m)(?![a-z])/i);
  if (hoursOnly && minsOnly) {
    const h = parseFloat(hoursOnly[1]);
    const m = parseFloat(minsOnly[1]);
    return `${trimTrailingZero(h)}h${String(Math.round(m)).padStart(2, "0")}`;
  }
  if (hoursOnly) {
    const h = parseFloat(hoursOnly[1]);
    if (Number.isFinite(h)) {
      // Whole hours → "2h"; decimal hours → convert to minutes.
      if (Number.isInteger(h)) return `${h}h`;
      const totalMin = Math.round(h * 60);
      return `${totalMin} min`;
    }
  }
  if (minsOnly) {
    const m = parseFloat(minsOnly[1]);
    if (Number.isFinite(m) && m >= 5) return `${Math.round(m)} min`;
  }
  return null;
}


function trimTrailingZero(n) {
  return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(2)));
}
